import {
  SEED_USAGE_SUMMARY,
  type UsageDailyRow,
  type UsagePeriodDays,
  type UsageSummarySeed,
} from '@/versions/v1/shared/settings-seed';

export const DAILY_DISPLAY_CAP = 14;

function scaleNumber(value: number, factor: number, decimals = 2): number {
  const scaled = value * factor;
  if (decimals === 0) return Math.round(scaled);
  return Number(scaled.toFixed(decimals));
}

/** Rescale daily weights so cost/turn sums match the period headline totals. */
function normalizeDailySeries(
  daily: UsageDailyRow[],
  targetCostUsd: number,
  targetTurns: number,
): UsageDailyRow[] {
  if (daily.length === 0) return daily;

  const costWeightSum = daily.reduce((sum, row) => sum + row.costUsd, 0) || 1;
  const turnWeightSum = daily.reduce((sum, row) => sum + row.turns, 0) || 1;

  let costSoFar = 0;
  let turnsSoFar = 0;

  return daily.map((row, index) => {
    const isLast = index === daily.length - 1;
    if (isLast) {
      return {
        ...row,
        costUsd: Number((targetCostUsd - costSoFar).toFixed(2)),
        turns: Math.max(0, targetTurns - turnsSoFar),
      };
    }

    const costUsd = Number(((row.costUsd / costWeightSum) * targetCostUsd).toFixed(2));
    const turns = Math.max(0, Math.round((row.turns / turnWeightSum) * targetTurns));
    costSoFar += costUsd;
    turnsSoFar += turns;
    return { ...row, costUsd, turns };
  });
}

export function capDailyRowsForDisplay(
  daily: UsageDailyRow[],
  periodDays: UsagePeriodDays,
): { rows: UsageDailyRow[]; totalDays: number; capped: boolean } {
  const totalDays = daily.length;
  if (periodDays <= 7 || totalDays <= DAILY_DISPLAY_CAP) {
    return { rows: daily, totalDays, capped: false };
  }
  return { rows: daily.slice(-DAILY_DISPLAY_CAP), totalDays, capped: true };
}

export function deriveUsageForPeriod(days: UsagePeriodDays): UsageSummarySeed {
  const base = SEED_USAGE_SUMMARY;
  const factor = days / base.sinceDays;
  const chatCostUsd = scaleNumber(base.chatCostUsd, factor);
  const chatTurns = scaleNumber(base.chatTurns, factor, 0);
  const daily = normalizeDailySeries(base.daily.slice(-days), chatCostUsd, chatTurns);

  return {
    sinceDays: days,
    totalCostUsd: scaleNumber(base.totalCostUsd, factor),
    chatCostUsd,
    chatTurns,
    inputTokens: scaleNumber(base.inputTokens, factor, 0),
    outputTokens: scaleNumber(base.outputTokens, factor, 0),
    agentCostUsd: scaleNumber(base.agentCostUsd, factor),
    agentRuns: scaleNumber(base.agentRuns, factor, 0),
    byModel: base.byModel.map((row) => ({
      ...row,
      costUsd: scaleNumber(row.costUsd, factor),
      turns: scaleNumber(row.turns, factor, 0),
      inputTokens: scaleNumber(row.inputTokens, factor, 0),
      outputTokens: scaleNumber(row.outputTokens, factor, 0),
    })),
    daily,
    connectors: {
      totalInvocations: scaleNumber(base.connectors.totalInvocations, factor, 0),
      byConnector: base.connectors.byConnector.map((row) => ({
        ...row,
        invocations: scaleNumber(row.invocations, factor, 0),
      })),
    },
    embeddings: {
      costUsd: scaleNumber(base.embeddings.costUsd, factor),
      calls: scaleNumber(base.embeddings.calls, factor, 0),
      inputTokens: scaleNumber(base.embeddings.inputTokens, factor, 0),
    },
  };
}
